import { useCallback, useEffect, useRef, useState } from 'react'

export type CheckStatus = 'idle' | 'checking' | 'success' | 'error'

interface UseDebouncedCheckOptions<T> {
  /** Raw input value (e.g. what the user typed). */
  value: string
  /** Extract a canonical key from the raw value. Return '' to skip checking. */
  clean: (v: string) => string
  /** Async function that validates the cleaned value. */
  check: (cleaned: string) => Promise<T>
  /** Determine whether the check result counts as success. */
  isSuccess: (result: T) => boolean
  /** Debounce delay in ms. */
  debounceMs: number
}

interface UseDebouncedCheckResult<T> {
  status: CheckStatus
  data: T | undefined
  /** Immediately run the check (cancels pending debounce). */
  flush: () => void
}

/**
 * Debounced async validation hook.
 * Deduplicates by cleaned value, ignores stale responses via request counter.
 *
 * `clean`, `check`, and `isSuccess` are read through refs so callers can pass
 * inline arrow functions without re-firing the debounce timer on every parent
 * render. Without this, an unstable identity for any of those callbacks would
 * cancel the pending timeout each render and the check would never run.
 */
export function useDebouncedCheck<T>({
  value,
  clean,
  check,
  isSuccess,
  debounceMs,
}: UseDebouncedCheckOptions<T>): UseDebouncedCheckResult<T> {
  const [status, setStatus] = useState<CheckStatus>('idle')
  const [data, setData] = useState<T | undefined>()
  const lastChecked = useRef('')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const requestId = useRef(0)

  // Mutable refs that always hold the latest callbacks. Ref assignment during
  // render is intentional — refs are escape hatches for exactly this pattern.
  const cleanRef = useRef(clean)
  const checkRef = useRef(check)
  const isSuccessRef = useRef(isSuccess)
  cleanRef.current = clean
  checkRef.current = check
  isSuccessRef.current = isSuccess

  const runCheck = useCallback(async (cleaned: string) => {
    if (!cleaned || cleaned === lastChecked.current) return
    lastChecked.current = cleaned
    const id = ++requestId.current
    setStatus('checking')
    try {
      const result = await checkRef.current(cleaned)
      if (id !== requestId.current) return
      setData(result)
      setStatus(isSuccessRef.current(result) ? 'success' : 'error')
    } catch {
      if (id !== requestId.current) return
      lastChecked.current = ''
      setStatus('error')
    }
  }, [])

  useEffect(() => {
    const cleaned = cleanRef.current(value)
    if (!cleaned) {
      setStatus('idle')
      setData(undefined)
      lastChecked.current = ''
      return
    }
    if (cleaned === lastChecked.current) return

    setStatus('idle')
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => runCheck(cleaned), debounceMs)
    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [value, debounceMs, runCheck])

  // Invalidate pending requests on unmount
  useEffect(
    () => () => {
      requestId.current++
    },
    []
  )

  const flush = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    const cleaned = cleanRef.current(value)
    if (cleaned) runCheck(cleaned)
  }, [value, runCheck])

  return { status, data, flush }
}
